import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLists } from './ListContext';
import { Task, TaskContextType } from './TaskContext/types';
import { fetchTasksFromSupabase, fetchAllTasksFromSupabase } from './TaskContext/taskApi';
import { useTaskOperations } from './TaskContext/useTaskOperations';

// Re-export types for backward compatibility
export type { TaskStatus, Task } from './TaskContext/types';

const TaskContext = createContext<TaskContextType | undefined>(undefined);

export const TaskProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(false);
  const { session, user } = useAuth();
  const { activeListId } = useLists();
  const isAuthenticated = !!session;

  const {
    addTask,
    updateTask,
    deleteTask,
    updateTaskTime,
    reorderTasks,
    updateTaskStatus,
    taskToEdit,
    setTaskToEdit
  } = useTaskOperations(
    tasks,
    setTasks,
    allTasks,
    setAllTasks,
    activeTask,
    setActiveTask,
    user,
    activeListId,
    isAuthenticated
  );

  // Memoized fetchTasks
  const fetchTasks = React.useCallback(async (listId: string) => {
    setLoading(true);
    if (user) {
      try {
        const tasks = await fetchTasksFromSupabase(user.id, listId);
        setTasks(tasks);
      } catch (error) {
        console.error('Error loading tasks:', error);
        loadFromLocalStorage();
      } finally {
        setLoading(false);
      }
    } else {
      loadFromLocalStorage();
      setLoading(false);
    }
  // Ensure all dependencies are included, but `user` should be stable.
  }, [user, isAuthenticated]);

  // Load all tasks when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchAllTasks();
    } else {
      setAllTasks([]);
    }
  }, [isAuthenticated, user]);

  const fetchAllTasks = async () => {
    if (!user) {
      setAllTasks([]);
      return;
    }
    
    try {
      const tasks = await fetchAllTasksFromSupabase(user.id);
      setAllTasks(tasks);
    } catch (error) {
      console.error('Error loading all tasks:', error);
      setAllTasks([]);
    }
  };

  
  const loadFromLocalStorage = () => {
    if (!isAuthenticated) return;
    
    const storedTasks = localStorage.getItem('tasks');
    if (storedTasks) {
      try {
        const parsedTasks = JSON.parse(storedTasks);
        setTasks(parsedTasks.map((task: any) => ({
          ...task,
          dateCreated: task.created_at ? new Date(task.created_at) : new Date(),
          position: task.position ?? 0
        })));
      } catch (error) {
        console.error('Error parsing tasks from localStorage:', error);
        setTasks([]);
      }
    }
  };

  // Save tasks to local storage
  useEffect(() => {
    if (!user && isAuthenticated) {
      localStorage.setItem('tasks', JSON.stringify(tasks));
    }
  }, [tasks, user, isAuthenticated]);

  const resetTasks = () => {
    setActiveTask(null);
    setTaskToEdit(null);
    
    if (!user) {
      setTasks([]);
      setAllTasks([]);
      localStorage.removeItem('tasks');
    }
  };

  // Use the memoized fetchTasks in useEffect
  useEffect(() => {
    if (activeListId && isAuthenticated) {
      fetchTasks(activeListId);
    } else {
      setTasks([]);
    }
  }, [activeListId, isAuthenticated, fetchTasks]);

  return (
    <TaskContext.Provider
      value={{
        tasks,
        allTasks,
        activeTask,
        setActiveTask,
        addTask,
        updateTask,
        deleteTask,
        updateTaskTime,
        resetTasks,
        taskToEdit,
        setTaskToEdit,
        reorderTasks,
        updateTaskStatus,
        loading,
        fetchTasks,
        fetchAllTasks
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};

export const useTasks = () => {
  const context = useContext(TaskContext);
  if (context === undefined) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
};

export default TaskProvider;
